import { useMemo } from 'react';
import { groupByLogicalKey } from '../components/ProposalDashboard/v2/amendment/groupByLogicalKey';
import { useLCATs } from './useLCATs';
/**
 * useLcatHistory returns the amendment-version chain for a single labor
 * category, derived from the project's full LCAT list.
 *
 * The hook reuses the existing useLCATs cache (no new RPC). Given a logical
 * key OR a representative LCAT row, it returns:
 *   - `versions`: every row sharing that logical key, latest first.
 *   - `current`: the canonical / current version (is_current_version=true).
 *   - `hasHistory`: true when there's more than one version. The drawer
 *     uses this to decide whether to render the timeline at all.
 *
 * Why a hook and not inline grouping in the drawer:
 *   - Keeps the drawer presentational and pure
 *   - Lets the LCATs table preview "X versions" affordances using the same
 *     derivation, without recomputing
 *   - Makes the data path testable in isolation
 *
 * Falls back to matching by `Title` when `logicalKey` isn't set on the
 * input row (legacy / non-amendment-aware projects). This way the drawer
 * still renders something sensible against pre-PR-B.5 data.
 */ export function useLcatHistory(projectId, selected) {
    const { data } = useLCATs(projectId);
    return useMemo(()=>{
        if (!selected || !data?.laborCategories) {
            return {
                versions: [],
                current: null,
                hasHistory: false
            };
        }
        // Prefer logicalKey for grouping (set by the merge step). Fall back to
        // a normalized title match when the field isn't populated yet.
        const grouped = groupByLogicalKey(data.laborCategories);
        let key = selected.logicalKey?.trim();
        if (!key) {
            // Try to find a row in the input matching by title — if every row
            // has the same logicalKey via the merge step, this is a no-op; if
            // legacy data, this picks the closest match.
            const titleNorm = (selected.title ?? '').trim().toLowerCase();
            for (const k of grouped.allKeys){
                const head = grouped.currentByKey.get(k);
                if (head && (head.title ?? '').trim().toLowerCase() === titleNorm) {
                    key = k;
                    break;
                }
            }
        }
        if (!key) {
            return {
                versions: [
                    selected
                ],
                current: selected,
                hasHistory: false
            };
        }
        const versions = grouped.historyByKey.get(key) ?? [
            selected
        ];
        const current = grouped.currentByKey.get(key) ?? selected;
        return {
            versions,
            current,
            hasHistory: versions.length > 1
        };
    }, [
        data?.laborCategories,
        selected
    ]);
}
